import { createContext, useEffect, useState } from 'react';

import {
	dollarDecimalFilterParams,
	decimalFilterParams1,
	decimalFilterParams,
} from '../../filters';
import { getFiscalYear } from '../../utils';
import axiosInstance from '../../api/axiosInstance';

const ManagementReportContext = createContext();

export const ManagementReportProvider = ({ children }) => {
	const [fy, setFy] = useState(getFiscalYear(4));
	const [dataType, setDataType] = useState('division-billings-by-month');
	const [columnDefs, setColumnDefs] = useState(null);
	const [dataView, setDataView] = useState('authorized');

	// Get columns
	const getColumnDefs = (selectedFy) => {
		const year = selectedFy - 1;

		const date = new Date();
		const currentYear = date.getFullYear();
		const currentMonth = date.getMonth();

		const lasttwoDigit = selectedFy.toString().slice(-2);
		return [
			{
				field:
					dataType === 'division-billings-by-month'
						? 'Division'
						: dataType === 'supplier-billings-by-month'
							? 'Supplier'
							: dataType === 'customer-billings-by-month'
								? 'Customer'
								: dataType === 'country-billings-by-month'
									? 'Country'
									: dataType === 'salesrep-billings-by-month'
										? 'SalesRep'
										: dataType === 'capitalizing-the-billings-by-month'
											? 'Brand'
											: dataType === 'gpm-by-customer-by-month'
												? 'Customer'
												: dataType === 'gp$-by-customer-by-month'
													? 'Customer'
													: dataType === 'gpm-by-salesrep-by-month'
														? 'SalesRep'
														: '',
				headerName:
					dataType === 'division-billings-by-month'
						? 'Division'
						: dataType === 'supplier-billings-by-month'
							? 'Supplier'
							: dataType === 'customer-billings-by-month'
								? 'Customer'
								: dataType === 'country-billings-by-month'
									? 'Country'
									: dataType === 'salesrep-billings-by-month'
										? 'Sales Rep'
										: dataType === 'capitalizing-the-billings-by-month'
											? 'MFR Brand'
											: dataType === 'gpm-by-customer-by-month' ||
												  dataType === 'gp$-by-customer-by-month'
												? 'Customer'
												: dataType === 'gpm-by-salesrep-by-month'
													? 'Sales Rep'
													: '',
				pinned: 'left',
				filter: 'agMultiColumnFilter',
			},
			{
				field: 'totalBackorders',
				cellClass: 'numberType',
				headerName: `Total Backorders`,
				cellRenderer: (p) => {
					const val = Number(p.value || 0);
					return `$ ${val.toLocaleString('en-US')}`;
				},
				filterParams: dollarDecimalFilterParams,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
				hide:
					dataType === 'division-billings-by-month' ||
					dataType === 'supplier-billings-by-month' ||
					dataType === 'country-billings-by-month' ||
					dataType === 'capitalizing-the-billings-by-month' ||
					dataType === 'gpm-by-salesrep-by-month' ||
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month',
			},
			{
				field: `CurrY-2`,
				cellClass: 'numberType',
				headerName: `FY${lasttwoDigit - 2}`,
				cellRenderer: (p) => `$ ${p.value.toLocaleString('en-US')}`,
				filterParams: dollarDecimalFilterParams,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
				hide:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month' ||
					dataType === 'customer-billings-by-month',
			},
			{
				field: `CurrY-1`,
				cellClass: 'numberType',
				headerName: `FY${lasttwoDigit - 1}`,
				cellRenderer: (p) => `$ ${p.value.toLocaleString('en-US')}`,
				filterParams: dollarDecimalFilterParams,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
				hide:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month' ||
					dataType === 'customer-billings-by-month',
			},
			{
				field: 'CurrY',
				headerName: `FY${lasttwoDigit}`,
				cellClass: 'numberType',
				cellRenderer: (p) => `$ ${p.value.toLocaleString('en-US')}`,
				filterParams: dollarDecimalFilterParams,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
				sort: 'desc',
				hide:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month',
			},
			{
				field: 'SalesTarget',
				headerName: `FY${lasttwoDigit} Target`,
				hide: dataType !== 'salesrep-billings-by-month',
				cellClass: 'numberType',
				cellRenderer: (p) => `$ ${p.value?.toLocaleString('en-US')}`,
				filterParams: dollarDecimalFilterParams,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'PctToGoal',
				headerName: `FY${lasttwoDigit} %-to-Goal`,
				hide: dataType !== 'salesrep-billings-by-month',
				cellClass: 'decimal',
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
				filterParams: decimalFilterParams,
			},
			{
				field: 'Apr',
				headerName: `April ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 3,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'May',
				headerName: `May ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 4,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Jun',
				headerName: `June ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 5,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Jul',
				headerName: `July ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 6,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Aug',
				headerName: `August ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 7,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Sep',
				headerName: `September ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 8,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Oct',
				headerName: `October ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 9,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Nov',
				headerName: `November ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 10,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Dec',
				headerName: `December ${year}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide: currentYear === year && currentMonth < 11,
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Jan',
				headerName: `January ${year + 1}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide:
					currentYear < year + 1 ||
					(currentYear === year + 1 && currentMonth < 0),
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Feb',
				headerName: `February ${year + 1}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',
				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide:
					currentYear < year + 1 ||
					(currentYear === year + 1 && currentMonth < 1),
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
			{
				field: 'Mar',
				headerName: `March ${year + 1}`,
				cellClass:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gp$-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? 'decimal'
						: 'numberType',

				cellRenderer: (p) =>
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? p.value
						: dataType === 'gp$-by-customer-by-month'
							? p.value.toLocaleString('en-US')
							: ` $ ${p.value.toLocaleString('en-US')}`,
				filterParams:
					dataType === 'gpm-by-customer-by-month' ||
					dataType === 'gpm-by-salesrep-by-month'
						? decimalFilterParams1
						: dataType === 'gp$-by-customer-by-month'
							? decimalFilterParams
							: dollarDecimalFilterParams,
				hide:
					currentYear < year + 1 ||
					(currentYear === year + 1 && currentMonth < 2),
				cellStyle: {
					borderRight: '1px solid #EAECEE',
				},
			},
		];
	};

	// const transformDataArray = (apiData) => {
	// 	const requiredKeysWithDefaults = {
	// 		CurrY: 0,
	// 		Apr: 0,
	// 		May: 0,
	// 		Jun: 0,
	// 		Jul: 0,
	// 		Aug: 0,
	// 		Sep: 0,
	// 		Oct: 0,
	// 		Nov: 0,
	// 		Dec: 0,
	// 		Jan: 0,
	// 		Feb: 0,
	// 		Mar: 0,
	// 		'CurrY-1': 0,
	// 		'CurrY-2': 0,
	// 		'CurrY-3': 0,
	// 	};

	// 	return apiData.map((item) => {
	// 		const transformedItem = {};

	// 		for (const key in item) {
	// 			transformedItem[key] = item[key];
	// 		}

	// 		for (const key in requiredKeysWithDefaults) {
	// 			if (!(key in item)) {
	// 				transformedItem[key] = requiredKeysWithDefaults[key];
	// 			} else {
	// 				transformedItem[key] = parseFloat(item[key]);
	// 			}
	// 		}

	// 		return transformedItem;
	// 	});
	// };

	const transformDataArray = (apiData, dataType) => {
		const requiredKeysWithDefaults = {
			CurrY: 0,
			Apr: 0,
			SalesTarget: 0,
			PctToGoal: 0,
			May: 0,
			Jun: 0,
			Jul: 0,
			Aug: 0,
			Sep: 0,
			Oct: 0,
			Nov: 0,
			Dec: 0,
			Jan: 0,
			Feb: 0,
			Mar: 0,
			'CurrY-1': 0,
			'CurrY-2': 0,
			'CurrY-3': 0,
			totalBackorders: 0,
		};

		return apiData.map((item) => {
			const transformedItem = {};

			for (const key in item) {
				transformedItem[key] = item[key];
			}

			for (const key in requiredKeysWithDefaults) {
				if (!(key in item)) {
					transformedItem[key] = requiredKeysWithDefaults[key];
				} else {
					let value = parseFloat(item[key]);
					if (isNaN(value)) {
						value = 0; // handle non-numeric values gracefully
					}

					if (
						dataType === 'gpm-by-customer-by-month' ||
						dataType === 'gpm-by-salesrep-by-month'
					) {
						transformedItem[key] = value.toFixed(2);
					} else if (dataType === 'gp$-by-customer-by-month') {
						transformedItem[key] = parseFloat(value.toFixed(2));
					} else {
						transformedItem[key] = parseFloat(value?.toFixed(2));
					}
				}
			}

			return transformedItem;
		});
	};

	const useReportData = (dataType, fy, dataView) => {
		const [data, setData] = useState([]);
		const [isLoading, setIsLoading] = useState(false);
		const [error, setError] = useState(null);
		const [divisionType, setDivisionType] = useState('');

		useEffect(() => {
			if (!dataType) return;

			const fetchData = async () => {
				setIsLoading(true);
				try {
					const response = await axiosInstance.get(
						`
						https://internal.supremecomponents.com/api/externalgateway.php?route=${
							dataType === 'division-billings-by-month'
								? 'get_division_billings_by_month'
								: dataType === 'supplier-billings-by-month'
									? 'get_supplier_billings_by_month'
									: dataType === 'customer-billings-by-month'
										? 'get_customer_billings_by_month'
										: dataType === 'country-billings-by-month'
											? 'get_country_billings_by_month'
											: dataType === 'salesrep-billings-by-month'
												? 'get_salesrep_billings_by_month'
												: dataType === 'capitalizing-the-billings-by-month'
													? 'get_capitalizing_billings_by_month'
													: dataType === 'gpm-by-customer-by-month'
														? 'get_gpm_by_customers_by_month'
														: dataType === 'gp$-by-customer-by-month'
															? 'get_gp_by_customers_by_month'
															: dataType === 'gpm-by-salesrep-by-month'
																? 'get_gpm_by_salesrep_by_month'
																: ''
						}&curryear=${fy - 1}&dataview=${dataView}`,
					);
					let transformedData = transformDataArray(
						response.data?.data,
						dataType,
					);

					// If type = customer-billings-by-month / salesrep-billings-by-month → fetch backorders
					if (
						dataType === 'customer-billings-by-month' ||
						dataType === 'salesrep-billings-by-month'
					) {
						const backordersResp = await axiosInstance.get(
							`https://internal.supremecomponents.com/zeeshanapi/api/backorders?is_admin=true`,
						);

						// Keep only non-excluded
						const backordersData = (backordersResp.data?.data || []).filter(
							(line) => (line.exclude || '').toLowerCase() === 'no',
						);

						// Add totalBackorders to each record
						transformedData = transformedData.map((item) => {
							let totalBackorderValue = 0;

							if (dataType === 'customer-billings-by-month') {
								const customerCode = (item.Customer || '').toLowerCase();
								const customerName = (item.CustomerName || '').toLowerCase();

								const matchedBackorders = backordersData.filter((bo) => {
									const boCustomerCode = (bo.Customer || '').toLowerCase();
									const boCustomerName = (bo.CustomerName || '').toLowerCase();

									// Compare Customer == CustomerName
									return (
										(customerCode &&
											boCustomerName &&
											customerCode === boCustomerName) ||
										(customerName &&
											boCustomerCode &&
											customerName === boCustomerCode)
									);
								});

								totalBackorderValue = matchedBackorders.reduce((sum, bo) => {
									const price = parseFloat(
										bo.QuantityRemaining > 0
											? bo.QuantityRemaining * Number(bo.SoUnitPrice)
											: 0,
									);
									return sum + price;
								}, 0);
							}

							if (dataType === 'salesrep-billings-by-month') {
								const salesRep = (item.SalesRep || '').toLowerCase();

								const matchedBackorders = backordersData.filter(
									(bo) =>
										salesRep &&
										(bo.SoSalesRep || '').toLowerCase() === salesRep,
								);

								totalBackorderValue = matchedBackorders.reduce((sum, bo) => {
									const price = parseFloat(
										bo.QuantityRemaining > 0
											? bo.QuantityRemaining * Number(bo.SoUnitPrice)
											: 0,
									);
									return sum + price;
								}, 0);
							}

							return {
								...item,
								totalBackorders:
									parseFloat(totalBackorderValue.toFixed(2)) || 0,
							};
						});
					}

					/**
					 * * (Piyush requirement) Remove rows who have zero value for current FY
					 */
					setData(
						dataType === 'salesrep-billings-by-month'
							? transformedData.filter((rep) => Number(rep.SalesTarget) > 0) // Vick sir request to show only salesrep with target assigned
							: dataType === 'gpm-by-customer-by-month' ||
								  dataType === 'gp$-by-customer-by-month' ||
								  dataType === 'gpm-by-salesrep-by-month'
								? transformedData
								: dataType === 'customer-billings-by-month'
									? transformedData?.filter(
											(customer) =>
												!customer.Customer?.toUpperCase().includes(
													'SUPREME COMPONENTS',
												),
										)
									: transformedData.filter((el) => el.CurrY > 0),
					);

					setColumnDefs(getColumnDefs(fy, dataType));
					setError(null);
				} catch (err) {
					setError(err);
					setData([]);
				} finally {
					setIsLoading(false);
				}
			};

			fetchData();
		}, [dataType, fy, divisionType, dataView]);

		return { data, isLoading, error, setDivisionType };
	};

	const { data, isLoading, error } = useReportData(dataType, fy, dataView);

	return (
		<ManagementReportContext.Provider
			value={{
				fy,
				setFy,
				columnDefs,
				dataType,
				setDataType,
				isLoading,
				error,
				data,
				dataView,
				setDataView,
			}}
		>
			{children}
		</ManagementReportContext.Provider>
	);
};

export default ManagementReportContext;
